import { Injectable } from '@angular/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';

import { PeriodBarResult } from '../../../ui/components/period-bar/period-bar.component';
import { PredefinedTaskRepository } from '../repository/predefined-task.repository';
import { TaskRepository } from '../repository/task.repository';
import { ToDoRepository } from '../repository/todo.repository';
import { TaskEventProvider } from './events/task_event_provider';
import {
  TaskCreatedEvent,
  TaskRemovedEvent,
  TaskUpdatedEvent,
} from './events/task_events';
import { ToDoEventProvider } from './events/todo_event_provider';
import {
  ToDoCreatedEvent,
  ToDoRemovedEvent,
  ToDoUpdatedEvent,
} from './events/todo_events';
import { PredefinedTask } from './predefined-task';
import { Task } from './task';
import { ToDo, ToDoStatus } from './todo';

@Injectable()
export class ToDoCommands {
  constructor(
    private toDoRepository: ToDoRepository,
    private taskRepository: TaskRepository,
    private predefinedTaskRepository: PredefinedTaskRepository,
    private toDoEventProvider: ToDoEventProvider,
    private taskEventProvider: TaskEventProvider,
    private toastr: ToastrService,
  ) {
    this.taskEventProvider.events$.subscribe((event) => {
      if (!event.notify) {
        return;
      }

      if (event instanceof TaskCreatedEvent) {
        this.toastr.success('Tâche ajoutée avec succès');
      }
      if (event instanceof TaskUpdatedEvent) {
        this.toastr.success('Tâche modifiée avec succès');
      }
      if (event instanceof TaskRemovedEvent) {
        this.toastr.success('Tâche supprimée avec succès');
      }
    });

    this.toDoEventProvider.events$.subscribe((event) => {
      if (!event.notify) {
        return;
      }

      if (event instanceof ToDoCreatedEvent) {
        this.toastr.success('Une nouvelle tâche planifiée a été ajoutée');
      }
      if (event instanceof ToDoUpdatedEvent) {
        this.toastr.success('La tâche planifié a été modifiée');
      }
      if (event instanceof ToDoRemovedEvent) {
        this.toastr.success('La tâche planifiée a été supprimée');
      }
    });
  }

  get toDoEvents$() {
    return this.toDoEventProvider.events$;
  }

  async findAllPredefinedTasks(): Promise<PredefinedTask[]> {
    return this.predefinedTaskRepository.findAll();
  }

  async findAllTasks(dietitianId: string): Promise<Task[]> {
    return this.taskRepository.findAll(dietitianId);
  }

  async findAllInStatus(
    dietitianId: string,
    period: PeriodBarResult | undefined,
    patientId: string | undefined,
    status: ToDoStatus[],
    sort: 'asc' | 'desc',
    lastId?: string | undefined,
  ): Promise<ToDo[]> {
    return this.toDoRepository.findAllInStatus(
      dietitianId,
      period,
      patientId,
      status,
      sort,
      lastId,
    );
  }

  async countAtDate(dietitianId: string, date: Date) {
    return this.toDoRepository.countAtDate(
      dietitianId,
      new Date(date),
      new Date(date),
    );
  }

  async countIsLate(dietitianId: string, date: Date) {
    return this.toDoRepository.countIsLate(dietitianId, new Date(date));
  }

  async findAllLate(
    dietitianId: string,
    date: Date,
    sort: 'asc' | 'desc',
    lastId?: string | undefined,
  ) {
    return this.toDoRepository.findAllLate(dietitianId, date, sort, lastId);
  }

  async findAllInStatusAtDay(
    dietitianId: string,
    date: Date,
    status: ToDoStatus[],
    sort: 'asc' | 'desc',
    lastId?: string | undefined,
  ) {
    return this.toDoRepository.findAllInStatusAndDate(
      dietitianId,
      new Date(date),
      new Date(date),
      undefined,
      status,
      sort,
      lastId,
    );
  }

  async findAllInStatusAtWeek(
    dietitianId: string,
    date: Date,
    patientId: string | undefined,
    status: ToDoStatus[],
    sort: 'asc' | 'desc',
    lastId?: string | undefined,
  ) {
    const m = moment(date);
    const monday = m.clone().startOf('isoWeek').hours(0).minutes(0).seconds(0);
    const sunday = m.clone().endOf('isoWeek').hours(23).minutes(59).seconds(59);
    return this.toDoRepository.findAllInStatusAndDate(
      dietitianId,
      monday.toDate(),
      sunday.toDate(),
      patientId,
      status,
      sort,
      lastId,
    );
  }

  async findAllToDosForPatients(
    dietitianId: string,
    status?: ToDoStatus,
  ): Promise<ToDo[]> {
    return this.toDoRepository.findAllForPatients(dietitianId, status);
  }

  async saveTask(task: Task): Promise<Task> {
    if (task.dietitianId) {
      if (task.id && task.id.toString() !== '_NEW_') {
        const updated = await this.taskRepository.update(
          task.dietitianId,
          task,
        );
        this.taskEventProvider.dispatch(new TaskUpdatedEvent(updated));
        return updated;
      } else {
        const created = await this.taskRepository.create(
          task.dietitianId,
          task,
        );
        this.taskEventProvider.dispatch(new TaskCreatedEvent(created));
        return created;
      }
    }
    throw new Error('DietitianId not provided');
  }

  async saveToDo(toDo: ToDo): Promise<ToDo> {
    if (toDo.dietitianId) {
      if (toDo.id && toDo.id.toString() !== '_NEW_') {
        const updated = await this.toDoRepository.update(
          toDo.dietitianId,
          toDo,
        );
        this.toDoEventProvider.dispatch(new ToDoUpdatedEvent(updated));
        return updated;
      } else {
        const created = await this.toDoRepository.create(
          toDo.dietitianId,
          toDo,
        );
        this.toDoEventProvider.dispatch(new ToDoCreatedEvent(created));
        return created;
      }
    }
    throw new Error('DietitianId not provided');
  }

  async cancelToDo(toDo: ToDo) {
    if (toDo.dietitianId) {
      await this.toDoRepository.delete(toDo.dietitianId, toDo);
      this.toDoEventProvider.dispatch(new ToDoRemovedEvent(toDo));
    }
  }

  async changeStatusToDo(toDo: ToDo) {
    if (toDo.dietitianId) {
      const changed = await this.toDoRepository.update(toDo.dietitianId, toDo);
      this.toDoEventProvider.dispatch(new ToDoUpdatedEvent(changed));
    }
  }
}
